/**
 * React hook for request/response streaming (NDJSON).
 *
 * Unlike {@link useStream} which opens a persistent connection,
 * this hook exposes a `send()` function that initiates a single
 * streaming request (e.g. POST a chat message, receive streamed response).
 *
 * Handles AbortController lifecycle, cleanup on unmount, and concurrent
 * request cancellation (starting a new request cancels the previous one).
 *
 * @example
 * ```tsx
 * const { send, isLoading, cancel, error } = useStreamRequest<ChatEvent>({
 *   protocol: EStreamProtocol.NDJSON,
 *   auth: { getToken: () => getAccessTokenSilently() },
 *   observability: { module: 'quill-chat', operation: 'send_message' },
 *   timeoutMs: 30_000,
 *   onMessage: (event) => handleChatEvent(event),
 *   onComplete: (events) => handleComplete(events),
 * });
 *
 * // Trigger a request:
 * await send({ url: '/api/chat', body: { message: 'Hello' } });
 * ```
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type IStreamConfig,
  type IStreamCallbacks,
  type IStreamError,
  type IRetryInfo,
  type IMessageMeta,
  EStreamProtocol,
} from '../types';
import { createStream } from '../stream-client';
import { STREAM_ABORT_REASON } from '../errors';

/**
 * Base configuration for the hook (applied to all requests).
 * URL, body, method, and headers can be overridden per send() call.
 */
export interface IUseStreamRequestOptions<T>
  extends Omit<IStreamConfig<T>, 'url'> {
  /** Called for each parsed message from the stream. */
  onMessage: (data: T, meta: IMessageMeta) => void;
  /** Called when a non-recoverable or exhausted error occurs. */
  onError?: (streamError: IStreamError) => void;
  /** Called when the stream completes normally. */
  onComplete?: (results: T[]) => void;
  /** Called before each retry attempt. */
  onRetry?: (info: IRetryInfo) => void;
}

/**
 * Per-request overrides passed to send().
 */
export interface ISendOptions {
  /** Request URL (required per request). */
  url: string;
  /** Request body. */
  body?: unknown;
  /** HTTP method override. */
  method?: string;
  /** Additional headers merged with base config. */
  headers?: Record<string, string>;
}

/**
 * Return value of the useStreamRequest hook.
 */
export interface IUseStreamRequestReturn<T> {
  /** Initiate a streaming request. Cancels any in-flight request. */
  send: (options: ISendOptions) => Promise<T[]>;
  /** Whether a request is currently in-flight. */
  isLoading: boolean;
  /** Cancel the current in-flight request. */
  cancel: () => void;
  /** Last error from a failed request. */
  error: IStreamError | null;
}

/**
 * React hook for on-demand streaming requests.
 *
 * @param baseOptions - Base config applied to all requests
 * @returns Request controls and state
 */
export function useStreamRequest<T>(
  baseOptions: IUseStreamRequestOptions<T>,
): IUseStreamRequestReturn<T> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<IStreamError | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Store callbacks in refs for stability
  const onMessageRef = useRef(baseOptions.onMessage);
  onMessageRef.current = baseOptions.onMessage;
  const onErrorRef = useRef(baseOptions.onError);
  onErrorRef.current = baseOptions.onError;
  const onCompleteRef = useRef(baseOptions.onComplete);
  onCompleteRef.current = baseOptions.onComplete;
  const onRetryRef = useRef(baseOptions.onRetry);
  onRetryRef.current = baseOptions.onRetry;
  const baseOptionsRef = useRef(baseOptions);
  baseOptionsRef.current = baseOptions;

  const mountedRef = useRef(true);

  // eslint-disable-next-line no-restricted-syntax -- reason: Cancel in-flight stream on unmount to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      abortControllerRef.current?.abort(STREAM_ABORT_REASON);
      abortControllerRef.current = null;
    };
  }, []);

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(STREAM_ABORT_REASON);
      abortControllerRef.current = null;
    }
  }, []);

  const send = useCallback(
    (sendOptions: ISendOptions): Promise<T[]> => {
      // Cancel any in-flight request
      cancel();

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsLoading(true);
      setError(null);

      const opts = baseOptionsRef.current;

      return new Promise<T[]>((resolve, reject) => {
        let settled = false;
        const results: T[] = [];

        // When the controller is aborted (cancel() or unmount), createStream
        // classifies it as USER_INITIATED and returns without calling onError
        // or onComplete. We listen for abort directly so the Promise always
        // settles and isLoading is reset.
        controller.signal.addEventListener(
          'abort',
          () => {
            if (settled) return;
            settled = true;
            if (mountedRef.current) {
              setIsLoading(false);
            }
            reject(new DOMException('Stream request cancelled', 'AbortError'));
          },
          { once: true },
        );

        const streamConfig: IStreamConfig<T> = {
          url: sendOptions.url,
          protocol: opts.protocol ?? EStreamProtocol.NDJSON,
          method: sendOptions.method ?? opts.method,
          headers: { ...opts.headers, ...sendOptions.headers },
          body: sendOptions.body ?? opts.body,
          auth: opts.auth,
          retry: opts.retry,
          timeoutMs: opts.timeoutMs,
          fatalHttpStatuses: opts.fatalHttpStatuses,
          retryableHttpStatuses: opts.retryableHttpStatuses,
          observability: opts.observability,
          parse: opts.parse,
          signal: controller.signal,
          reconnectOnClose: false,
        };

        const callbacks: IStreamCallbacks<T> = {
          onMessage: (data, meta) => {
            results.push(data);
            onMessageRef.current?.(data, meta);
          },
          onStateChange: _state => {
            // State is managed via isLoading for request-style streams
          },
          onError: streamError => {
            if (settled) return;
            settled = true;
            if (mountedRef.current) {
              setError(streamError);
              setIsLoading(false);
            }
            onErrorRef.current?.(streamError);
            reject(streamError.error);
          },
          onComplete: () => {
            if (settled) return;
            settled = true;
            if (mountedRef.current) {
              setIsLoading(false);
            }
            onCompleteRef.current?.(results);
            resolve(results);
          },
          onRetry: info => onRetryRef.current?.(info),
        };

        createStream(streamConfig, callbacks);
      });
    },
    [cancel],
  );

  return {
    send,
    isLoading,
    cancel,
    error,
  };
}
